import React, { useState, useEffect, useRef } from 'react';
import { FaPlus, FaEdit, FaTrash, FaBuilding, FaCalendarAlt, FaCheckCircle, FaPause, FaTimesCircle, FaMoneyBillWave, FaTimes } from 'react-icons/fa';
import Swal from 'sweetalert2';
import arriendosService from '../services/arriendosService';
import { COMUNAS_SII, REGION_BY_CODIGO } from '../constants/comunasSII';
import YearSelector from '../components/common/YearSelector';
import useYearSelector from '../hooks/useYearSelector';
import '../styles/pages/ArriendosPage.css';

const TIPOS_AMOBLADO = [
    { value: '1', label: '1 - Sin amoblar' },
    { value: '2', label: '2 - Amoblado' }
];

const DESTINOS_BIEN_RAIZ = [
    { value: '1', label: '1 - Habitacional' },
    { value: '2', label: '2 - Comercial' },
    { value: '3', label: '3 - Estacionamiento' },
    { value: '4', label: '4 - Bodega' },
    { value: '5', label: '5 - Habitacional y comercial' },
    { value: '6', label: '6 - Otro destino' }
];

const NATURALEZAS_BIEN_RAIZ = [
    { value: '1', label: '1 - Agrícola' },
    { value: '2', label: '2 - No Agrícola' }
];

const TIPOS_COMPROBANTE = [
    { value: 'factura', label: 'Factura' },
    { value: 'boleta', label: 'Boleta' },
    { value: 'nada', label: 'Sin comprobante' }
];

const ESTADOS = [
    { value: 'activo', label: 'Activo', icon: <FaCheckCircle /> },
    { value: 'terminado', label: 'Terminado', icon: <FaTimesCircle /> },
    { value: 'suspendido', label: 'Suspendido', icon: <FaPause /> }
];

/**
 * Página de Arriendos
 * Gestión de propiedades y locales arrendados por la empresa
 * Preparado para MCP/Agentes autónomos
 */
const ArriendosPage = ({ token, id_company }) => {
    const [arriendos, setArriendos] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Estados para carga de PDF con IA
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [pdfInputRef] = useState(React.createRef());

    // Modal de pagos mensuales
    const [showPagosModal, setShowPagosModal] = useState(false);
    const [arriendoSeleccionado, setArriendoSeleccionado] = useState(null);
    const [pagosMensuales, setPagosMensuales] = useState([]);
    const [loadingPagos, setLoadingPagos] = useState(false);
    const [editandoMonto, setEditandoMonto] = useState(null);

    // Selector de año
    const { selectedYear, setSelectedYear, yearOptions, currentYear } = useYearSelector(null, 10, 1);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('activo');


    // Formulario
    const [formData, setFormData] = useState({
        direccion: '',
        rol_bien_raiz: '',
        comuna: '',
        codigo_comuna: '',
        region: '',
        amoblado: '',
        destino_bien_raiz: '',
        dfl2: false,
        naturaleza_bien_raiz: '',
        nombre_propietario: '',
        rut_propietario: '',
        telefono_propietario: '',
        email_propietario: '',
        valor_arriendo: '',
        moneda: 'CLP',
        dia_pago: '',
        fecha_inicio: '',
        fecha_termino: '',
        estado: 'activo',
        observaciones: '',
        contrato_url: '',
        comprobante: ''
    });

    // Autocomplete de comuna
    const [comunaSearch, setComunaSearch] = useState('');
    const [comunaDropdownOpen, setComunaDropdownOpen] = useState(false);
    const comunaRef = useRef(null);

    const comunasFiltradas = comunaSearch.length >= 2
        ? COMUNAS_SII.filter(c =>
            c.nombre.includes(comunaSearch.toUpperCase()) ||
            String(c.codigo).startsWith(comunaSearch)
        ).slice(0, 15)
        : [];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (comunaRef.current && !comunaRef.current.contains(e.target)) {
                setComunaDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (selectedYear) {
            fetchArriendos();
            fetchResumen();
        }
    }, [filtroEstado, selectedYear]);

    const fetchArriendos = async () => {
        try {
            setLoading(true);
            const params = {
                id_company,
                estado: filtroEstado,
                anio: selectedYear
            };

            const data = await arriendosService.getArriendos(params, token);
            setArriendos(data);
        } catch (error) {
            console.error('Error al cargar arriendos:', error);
            Swal.fire('Error', 'No se pudieron cargar los arriendos', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchResumen = async () => {
        try {
            const data = await arriendosService.getResumen(selectedYear, id_company, token);
            setResumen(data);
        } catch (error) {
            console.error('Error al cargar resumen:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelectComuna = (comuna) => {
        setFormData(prev => ({
            ...prev,
            comuna: comuna.nombre,
            codigo_comuna: comuna.codigo,
            region: REGION_BY_CODIGO[comuna.codigo] || ''
        }));
        setComunaSearch(comuna.nombre);
        setComunaDropdownOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const arriendoData = {
                ...formData,
                id_company,
                valor_arriendo: parseFloat(formData.valor_arriendo),
                dia_pago: formData.dia_pago ? parseInt(formData.dia_pago) : null
            };

            if (editingId) {
                await arriendosService.updateArriendo(editingId, arriendoData, token);
                Swal.fire('Éxito', 'Arriendo actualizado correctamente', 'success');
            } else {
                await arriendosService.createArriendo(arriendoData, token);
                Swal.fire('Éxito', 'Arriendo registrado correctamente', 'success');
            }

            resetForm();
            fetchArriendos();
            fetchResumen();
        } catch (error) {
            console.error('Error al guardar arriendo:', error);
            Swal.fire('Error', error.message || 'Error al guardar arriendo', 'error');
        }
    };

    const handleEdit = (arriendo) => {
        setComunaSearch(arriendo.comuna || '');
        setFormData({
            direccion: arriendo.direccion,
            rol_bien_raiz: arriendo.rol_bien_raiz || '',
            comuna: arriendo.comuna || '',
            codigo_comuna: arriendo.codigo_comuna || '',
            region: arriendo.region || '',
            amoblado: arriendo.amoblado || '',
            destino_bien_raiz: arriendo.destino_bien_raiz || '',
            dfl2: arriendo.dfl2 || false,
            naturaleza_bien_raiz: arriendo.naturaleza_bien_raiz || '',
            nombre_propietario: arriendo.nombre_propietario || '',
            rut_propietario: arriendo.rut_propietario || '',
            telefono_propietario: arriendo.telefono_propietario || '',
            email_propietario: arriendo.email_propietario || '',
            valor_arriendo: arriendo.valor_arriendo,
            moneda: arriendo.moneda || 'CLP',
            dia_pago: arriendo.dia_pago || '',
            fecha_inicio: arriendo.fecha_inicio ? arriendo.fecha_inicio.split('T')[0] : '',
            fecha_termino: arriendo.fecha_termino ? arriendo.fecha_termino.split('T')[0] : '',
            estado: arriendo.estado,
            observaciones: arriendo.observaciones || '',
            contrato_url: arriendo.contrato_url || '',
            comprobante: arriendo.comprobante || ''
        });
        setEditingId(arriendo.id_arriendo);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await arriendosService.deleteArriendo(id, id_company, token);
                Swal.fire('Eliminado', 'Arriendo eliminado correctamente', 'success');
                fetchArriendos();
                fetchResumen();
            } catch (error) {
                console.error('Error al eliminar:', error);
                Swal.fire('Error', error.message || 'Error al eliminar arriendo', 'error');
            }
        }
    };

    const handleCambiarEstado = async (id, nuevoEstado) => {
        try {
            await arriendosService.cambiarEstado(id, { id_company, estado: nuevoEstado }, token);
            Swal.fire('Éxito', `Estado cambiado a ${nuevoEstado}`, 'success');
            fetchArriendos();
            fetchResumen();
        } catch (error) {
            console.error('Error al cambiar estado:', error);
            Swal.fire('Error', error.message || 'Error al cambiar estado', 'error');
        }
    };

    const resetForm = () => {
        setComunaSearch('');
        setComunaDropdownOpen(false);
        setFormData({
            direccion: '',
            rol_bien_raiz: '',
            comuna: '',
            codigo_comuna: '',
            region: '',
            amoblado: '',
            destino_bien_raiz: '',
            dfl2: false,
            naturaleza_bien_raiz: '',
            nombre_propietario: '',
            rut_propietario: '',
            telefono_propietario: '',
            email_propietario: '',
            valor_arriendo: '',
            moneda: 'CLP',
            dia_pago: '',
            fecha_inicio: '',
            fecha_termino: '',
            estado: 'activo',
            observaciones: '',
            contrato_url: '',
            comprobante: ''
        });
        setEditingId(null);
        setShowModal(false);
    };

    /**
     * Manejar carga de PDF y extracción automática con IA
     */
    const handleCargarPdf = () => {
        pdfInputRef.current.click();
    };

    const handlePdfSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar que sea PDF
        if (file.type !== 'application/pdf') {
            Swal.fire('Error', 'El archivo debe ser un PDF', 'error');
            return;
        }

        try {
            setLoadingPdf(true);

            // Mostrar mensaje de carga
            Swal.fire({
                title: 'Extrayendo datos del PDF...',
                html: 'Claude está analizando el contrato. Esto puede tomar unos segundos.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Llamar al servicio para extraer datos
            const result = await arriendosService.extractFromPdf(file, id_company, token);

            Swal.close();

            if (result.success) {
                // Mostrar resumen de extracción
                const fieldsCount = result.fieldsExtracted.length;
                const totalFields = result.fieldsExtracted.length + result.fieldsMissing.length;

                await Swal.fire({
                    title: 'Datos extraídos',
                    html: `
                        <p><strong>${fieldsCount} de ${totalFields} campos</strong> fueron extraídos del PDF.</p>
                        <p class="text-muted" style="font-size: 14px; margin-top: 10px;">
                            Los campos faltantes quedarán en blanco para que los completes manualmente.
                        </p>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Continuar'
                });

                // Prellenar formulario con datos extraídos
                const extractedData = result.data;

                // Intentar encontrar la comuna en la lista SII (normalizar a mayúsculas)
                let comunaMatch = null;
                if (extractedData.comuna) {
                    const searchStr = extractedData.comuna.toUpperCase().trim();
                    comunaMatch = COMUNAS_SII.find(c => c.nombre === searchStr);
                }

                setComunaSearch(comunaMatch ? comunaMatch.nombre : (extractedData.comuna || ''));

                // Mapeo de fallback: si la IA retornó texto en vez de código numérico
                // Defaults: amoblado=1 (sin amoblar), destino=2 (comercial), naturaleza=2 (no agrícola)
                const mapAmoblado = (v) => {
                    if (!v) return '1';
                    if (v === '1' || v === '2') return v;
                    const l = v.toLowerCase();
                    if (l.includes('sin amoblar')) return '1';
                    if (l.includes('amoblado') || l.includes('mueble')) return '2';
                    return '1';
                };
                const mapDestino = (v) => {
                    if (!v) return '2';
                    if (['1','2','3','4','5','6'].includes(String(v))) return String(v);
                    const l = v.toLowerCase();
                    if (l.includes('habitacional y comercial') || l.includes('mixto')) return '5';
                    if (l.includes('habitacional') || l.includes('residencial')) return '1';
                    if (l.includes('comercial') || l.includes('oficina')) return '2';
                    if (l.includes('estacionamiento') || l.includes('garaje')) return '3';
                    if (l.includes('bodega') || l.includes('depósito')) return '4';
                    return '2';
                };
                const mapNaturaleza = (v) => {
                    if (!v) return '2';
                    if (v === '1' || v === '2') return v;
                    const l = v.toLowerCase();
                    if (l.includes('no agrícola') || l.includes('no agricola')) return '2';
                    if (l.includes('agrícola') || l.includes('agricola')) return '1';
                    return '2';
                };

                // Actualizar formulario
                setFormData(prev => ({
                    ...prev,
                    direccion: extractedData.direccion || '',
                    rol_bien_raiz: extractedData.rol_bien_raiz || '',
                    comuna: comunaMatch ? comunaMatch.nombre : (extractedData.comuna || ''),
                    codigo_comuna: comunaMatch ? comunaMatch.codigo : '',
                    region: comunaMatch ? (REGION_BY_CODIGO[comunaMatch.codigo] || '') : (extractedData.region || ''),
                    amoblado: mapAmoblado(extractedData.amoblado),
                    destino_bien_raiz: mapDestino(extractedData.destino_bien_raiz),
                    dfl2: extractedData.dfl2 || false,
                    naturaleza_bien_raiz: mapNaturaleza(extractedData.naturaleza_bien_raiz),
                    nombre_propietario: extractedData.nombre_propietario || '',
                    rut_propietario: extractedData.rut_propietario || '',
                    telefono_propietario: extractedData.telefono_propietario || '',
                    email_propietario: extractedData.email_propietario || '',
                    valor_arriendo: extractedData.valor_arriendo || '',
                    moneda: extractedData.moneda || 'CLP',
                    dia_pago: extractedData.dia_pago || '',
                    fecha_inicio: extractedData.fecha_inicio || '',
                    fecha_termino: extractedData.fecha_termino || '',
                    estado: extractedData.estado || 'activo',
                    observaciones: extractedData.observaciones || '',
                    comprobante: extractedData.comprobante || '',
                    contrato_url: extractedData.contrato_url || ''
                }));

                // Abrir modal con formulario prellenado
                setShowModal(true);

            } else {
                Swal.fire(
                    'Error',
                    result.message || 'No se pudo extraer datos del PDF',
                    'error'
                );
            }

        } catch (error) {
            console.error('Error al procesar PDF:', error);
            Swal.fire(
                'Error',
                error.message || 'Error al procesar el PDF',
                'error'
            );
        } finally {
            setLoadingPdf(false);
            // Limpiar input para permitir seleccionar el mismo archivo nuevamente
            e.target.value = '';
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP'
        }).format(value);
    };

    // Formatear fecha local para guardar (sin conversión a UTC)
    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Formatear fecha string de BD para mostrar (evita problemas de zona horaria)
    const formatDateStringToDisplay = (dateString) => {
        if (!dateString) return '-';
        // Extraer solo la parte de la fecha (YYYY-MM-DD)
        const datePart = dateString.split('T')[0];
        const [year, month, day] = datePart.split('-');
        // Crear fecha local sin conversión UTC
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('es-CL');
    };

    // ============================================
    // FUNCIONES PARA PAGOS MENSUALES
    // ============================================

    const handleVerPagos = async (arriendo) => {
        try {
            setArriendoSeleccionado(arriendo);
            setShowPagosModal(true);
            setLoadingPagos(true);

            const data = await arriendosService.getPagosArriendo(
                arriendo.id_arriendo,
                selectedYear,
                id_company,
                token
            );

            setPagosMensuales(data.pagos);
        } catch (error) {
            console.error('Error al cargar pagos:', error);
            Swal.fire('Error', 'No se pudieron cargar los pagos mensuales', 'error');
        } finally {
            setLoadingPagos(false);
        }
    };

    const handleTogglePago = async (pago) => {
        try {
            const nuevoEstado = pago.estado === 'pagado' ? 'pendiente' : 'pagado';
            const fechaPago = nuevoEstado === 'pagado' ? formatLocalDate(new Date()) : null;

            await arriendosService.actualizarPago(
                arriendoSeleccionado.id_arriendo,
                pago.id_pago,
                {
                    id_company,
                    estado: nuevoEstado,
                    fecha_pago: fechaPago
                },
                token
            );

            // Actualizar el estado local
            setPagosMensuales(prevPagos =>
                prevPagos.map(p =>
                    p.id_pago === pago.id_pago
                        ? { ...p, estado: nuevoEstado, fecha_pago: fechaPago }
                        : p
                )
            );

            Swal.fire({
                title: 'Éxito',
                text: `Pago marcado como ${nuevoEstado}`,
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error al actualizar pago:', error);
            Swal.fire('Error', 'No se pudo actualizar el pago', 'error');
        }
    };

    const handleEditarMonto = (pago) => {
        setEditandoMonto({
            id_pago: pago.id_pago,
            monto: pago.monto
        });
    };

    const handleGuardarMonto = async (pago) => {
        try {
            if (!editandoMonto || editandoMonto.monto <= 0) {
                Swal.fire('Error', 'El monto debe ser mayor a 0', 'error');
                return;
            }

            await arriendosService.cambiarMontoPago(
                arriendoSeleccionado.id_arriendo,
                pago.id_pago,
                parseFloat(editandoMonto.monto),
                id_company,
                token
            );

            // Actualizar el estado local
            setPagosMensuales(prevPagos =>
                prevPagos.map(p =>
                    p.id_pago === pago.id_pago
                        ? { ...p, monto: parseFloat(editandoMonto.monto) }
                        : p
                )
            );

            setEditandoMonto(null);

            Swal.fire({
                title: 'Éxito',
                text: 'Monto actualizado correctamente',
                icon: 'success',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error al actualizar monto:', error);
            Swal.fire('Error', 'No se pudo actualizar el monto', 'error');
        }
    };

    const handleCancelarEdicion = () => {
        setEditandoMonto(null);
    };

    const cerrarModalPagos = () => {
        setShowPagosModal(false);
        setArriendoSeleccionado(null);
        setPagosMensuales([]);
        setEditandoMonto(null);
    };

    return (
        <div className="arriendos-page">
            <div className="page-header">
                <div className="header-left">
                    <FaBuilding className="page-icon" />
                    <div>
                        <h1>Arriendos</h1>
                        <p>Gestión de propiedades y locales arrendados</p>
                    </div>
                </div>
                <div className="header-right">
                    <YearSelector
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        yearOptions={yearOptions}
                        label="Filtrar por año"
                        showIcon={true}
                    />
                    <button
                        className="btn-secondary"
                        onClick={handleCargarPdf}
                        title="Cargar contrato PDF y extraer datos automáticamente con IA"
                    >
                        <FaPlus /> Cargar desde PDF
                    </button>
                    <button className="btn-primary" onClick={() => setShowModal(true)}>
                        <FaPlus /> Nuevo Arriendo
                    </button>
                    {/* Input file oculto para PDFs */}
                    <input
                        type="file"
                        ref={pdfInputRef}
                        accept="application/pdf"
                        onChange={handlePdfSelected}
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            {/* Resumen */}
            {resumen && (
                <div className="resumen-cards">
                    <div className="resumen-card">
                        <h3>Total Arriendos</h3>
                        <p className="value">{resumen.total_arriendos}</p>
                    </div>
                    <div className="resumen-card active">
                        <h3>Arriendos Activos</h3>
                        <p className="value">{resumen.arriendos_activos}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Total Mensual</h3>
                        <p className="value">{formatCurrency(resumen.total_mensual)}</p>
                    </div>
                    <div className="resumen-card">
                        <h3>Proyección Anual</h3>
                        <p className="value">{formatCurrency(resumen.total_anual_proyectado)}</p>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="filters">
                <div className="filter-group">
                    <label>Estado:</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        {ESTADOS.map(estado => (
                            <option key={estado.value} value={estado.value}>{estado.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tabla de arriendos */}
            <div className="table-container">
                {loading ? (
                    <div className="loading">Cargando arriendos...</div>
                ) : arriendos.length === 0 ? (
                    <div className="empty-state">
                        <FaBuilding size={48} />
                        <p>No hay arriendos registrados para este filtro</p>
                    </div>
                ) : (
                    <table className="arriendos-table">
                        <thead>
                            <tr>
                                <th>Dirección</th>
                                <th>Comuna</th>
                                <th>Destino</th>
                                <th>Propietario</th>
                                <th>Valor</th>
                                <th>Comprobante</th>
                                <th>Día Pago</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Término</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {arriendos.map(arriendo => (
                                <tr
                                    key={arriendo.id_arriendo}
                                    className="clickable-row"
                                    onClick={() => handleVerPagos(arriendo)}
                                    title="Click para ver pagos mensuales"
                                >
                                    <td>{arriendo.direccion}</td>
                                    <td>{arriendo.comuna || '-'}</td>
                                    <td>{arriendo.destino_bien_raiz || '-'}</td>
                                    <td>
                                        {arriendo.nombre_propietario || '-'}
                                        {arriendo.rut_propietario && <div className="sub-text">{arriendo.rut_propietario}</div>}
                                    </td>
                                    <td className="currency">{formatCurrency(arriendo.valor_arriendo)}</td>
                                    <td>
                                        <span className={`badge badge-${arriendo.comprobante || 'default'}`}>
                                            {arriendo.comprobante === 'factura' ? 'Factura' :
                                             arriendo.comprobante === 'boleta' ? 'Boleta' :
                                             arriendo.comprobante === 'nada' ? 'Sin comprobante' : 'Nada'}
                                        </span>
                                    </td>
                                    <td>{arriendo.dia_pago || '-'}</td>
                                    <td>{new Date(arriendo.fecha_inicio).toLocaleDateString('es-CL')}</td>
                                    <td>{arriendo.fecha_termino ? new Date(arriendo.fecha_termino).toLocaleDateString('es-CL') : 'Indefinido'}</td>
                                    <td>
                                        <span className={`badge badge-${arriendo.estado}`}>
                                            {arriendo.estado}
                                        </span>
                                    </td>
                                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className="btn-icon btn-edit"
                                            onClick={() => handleEdit(arriendo)}
                                            title="Editar"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            className="btn-icon btn-delete"
                                            onClick={() => handleDelete(arriendo.id_arriendo)}
                                            title="Eliminar"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={resetForm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{editingId ? 'Editar Arriendo' : 'Nuevo Arriendo'}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-grid">
                                <div className="form-group full-width">
                                    <label>Dirección *</label>
                                    <input
                                        type="text"
                                        name="direccion"
                                        value={formData.direccion}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Rol del Bien Raíz</label>
                                    <input
                                        type="text"
                                        name="rol_bien_raiz"
                                        value={formData.rol_bien_raiz}
                                        onChange={handleInputChange}
                                        placeholder="Ej: 123-456"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Región</label>
                                    <input
                                        type="text"
                                        name="region"
                                        value={formData.region}
                                        readOnly
                                        placeholder="Se completa al seleccionar comuna"
                                        style={{ backgroundColor: '#f5f5f5', cursor: 'default' }}
                                    />
                                </div>

                                <div className="form-group" ref={comunaRef} style={{ position: 'relative' }}>
                                    <label>Comuna</label>
                                    <input
                                        type="text"
                                        value={comunaSearch}
                                        onChange={(e) => {
                                            setComunaSearch(e.target.value);
                                            setComunaDropdownOpen(true);
                                            if (!e.target.value) {
                                                setFormData(prev => ({ ...prev, comuna: '', codigo_comuna: '', region: '' }));
                                            }
                                        }}
                                        onFocus={() => comunaSearch.length >= 2 && setComunaDropdownOpen(true)}
                                        placeholder="Buscar comuna..."
                                        autoComplete="off"
                                    />
                                    {comunaDropdownOpen && comunasFiltradas.length > 0 && (
                                        <ul className="comuna-dropdown">
                                            {comunasFiltradas.map(c => (
                                                <li key={c.codigo} onMouseDown={() => handleSelectComuna(c)}>
                                                    <span className="comuna-nombre">{c.nombre}</span>
                                                    <span className="comuna-codigo">{c.codigo}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Amoblado</label>
                                    <select
                                        name="amoblado"
                                        value={formData.amoblado}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Seleccione...</option>
                                        {TIPOS_AMOBLADO.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Destino del Bien Raíz</label>
                                    <select
                                        name="destino_bien_raiz"
                                        value={formData.destino_bien_raiz}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Seleccione...</option>
                                        {DESTINOS_BIEN_RAIZ.map(destino => (
                                            <option key={destino.value} value={destino.value}>{destino.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Naturaleza del Bien Raíz</label>
                                    <select
                                        name="naturaleza_bien_raiz"
                                        value={formData.naturaleza_bien_raiz}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Seleccione...</option>
                                        {NATURALEZAS_BIEN_RAIZ.map(naturaleza => (
                                            <option key={naturaleza.value} value={naturaleza.value}>{naturaleza.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="dfl2"
                                            checked={formData.dfl2}
                                            onChange={handleInputChange}
                                            style={{ width: 'auto', marginRight: '8px' }}
                                        />
                                        DFL-2
                                    </label>
                                </div>

                                <div className="form-group">
                                    <label>Estado</label>
                                    <select
                                        name="estado"
                                        value={formData.estado}
                                        onChange={handleInputChange}
                                    >
                                        {ESTADOS.map(estado => (
                                            <option key={estado.value} value={estado.value}>{estado.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Comprobante *</label>
                                    <select
                                        name="comprobante"
                                        value={formData.comprobante}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleccione...</option>
                                        {TIPOS_COMPROBANTE.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Nombre Propietario</label>
                                    <input
                                        type="text"
                                        name="nombre_propietario"
                                        value={formData.nombre_propietario}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>RUT Propietario</label>
                                    <input
                                        type="text"
                                        name="rut_propietario"
                                        value={formData.rut_propietario}
                                        onChange={handleInputChange}
                                        placeholder="12.345.678-9"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Teléfono</label>
                                    <input
                                        type="tel"
                                        name="telefono_propietario"
                                        value={formData.telefono_propietario}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email_propietario"
                                        value={formData.email_propietario}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Valor Arriendo *</label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        name="valor_arriendo"
                                        value={formData.valor_arriendo === '' ? '' : Number(formData.valor_arriendo).toLocaleString('es-CL')}
                                        onChange={e => handleInputChange({
                                            target: { name: 'valor_arriendo', value: e.target.value.replace(/\D/g, ''), type: 'text' }
                                        })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Día de Pago</label>
                                    <input
                                        type="number"
                                        name="dia_pago"
                                        value={formData.dia_pago}
                                        onChange={handleInputChange}
                                        min="1"
                                        max="31"
                                        placeholder="1-31"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Inicio *</label>
                                    <input
                                        type="date"
                                        name="fecha_inicio"
                                        value={formData.fecha_inicio}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Fecha Término</label>
                                    <input
                                        type="date"
                                        name="fecha_termino"
                                        value={formData.fecha_termino}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>URL Contrato</label>
                                    <input
                                        type="url"
                                        name="contrato_url"
                                        value={formData.contrato_url}
                                        onChange={handleInputChange}
                                        placeholder="https://..."
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label>Observaciones</label>
                                    <textarea
                                        name="observaciones"
                                        value={formData.observaciones}
                                        onChange={handleInputChange}
                                        rows="3"
                                    />
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={resetForm}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">
                                    {editingId ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Pagos Mensuales */}
            {showPagosModal && arriendoSeleccionado && (
                <div className="modal-overlay" onClick={cerrarModalPagos}>
                    <div className="modal-content modal-pagos" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div>
                                <h2>
                                    <FaMoneyBillWave /> Pagos Mensuales {selectedYear}
                                </h2>
                                <p className="modal-subtitle">
                                    {arriendoSeleccionado.direccion} - {arriendoSeleccionado.tipo_propiedad}
                                </p>
                            </div>
                            <button className="close-btn" onClick={cerrarModalPagos}>
                                <FaTimes />
                            </button>
                        </div>

                        {loadingPagos ? (
                            <div className="loading">Cargando pagos...</div>
                        ) : (
                            <div className="pagos-container">
                                <div className="pagos-info">
                                    <div className="info-item">
                                        <span className="label">Valor Base:</span>
                                        <span className="value">{formatCurrency(arriendoSeleccionado.valor_arriendo)}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Día de Pago:</span>
                                        <span className="value">{arriendoSeleccionado.dia_pago || 'No especificado'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Pagados:</span>
                                        <span className="value success">
                                            {pagosMensuales.filter(p => p.estado === 'pagado').length} / 12
                                        </span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">Pendientes:</span>
                                        <span className="value warning">
                                            {pagosMensuales.filter(p => p.estado === 'pendiente').length} / 12
                                        </span>
                                    </div>
                                </div>

                                <div className="pagos-table-wrapper">
                                    <table className="pagos-table">
                                        <thead>
                                            <tr>
                                                <th>Mes</th>
                                                <th>Monto</th>
                                                <th>Estado</th>
                                                <th>Fecha Pago</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagosMensuales.map(pago => (
                                                <tr
                                                    key={pago.id_pago}
                                                    className={`pago-row ${pago.estado}`}
                                                >
                                                    <td className="mes-nombre">{pago.mes_nombre}</td>
                                                    <td className="monto-cell">
                                                        {editandoMonto && editandoMonto.id_pago === pago.id_pago ? (
                                                            <div className="monto-edit">
                                                                <input
                                                                    type="number"
                                                                    value={editandoMonto.monto}
                                                                    onChange={(e) => setEditandoMonto({
                                                                        ...editandoMonto,
                                                                        monto: e.target.value
                                                                    })}
                                                                    className="monto-input"
                                                                    min="0"
                                                                    step="1000"
                                                                />
                                                                <button
                                                                    className="btn-small btn-save"
                                                                    onClick={() => handleGuardarMonto(pago)}
                                                                >
                                                                    ✓
                                                                </button>
                                                                <button
                                                                    className="btn-small btn-cancel-small"
                                                                    onClick={handleCancelarEdicion}
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="monto-display">
                                                                <span className="monto-value">
                                                                    {formatCurrency(pago.monto)}
                                                                </span>
                                                                <button
                                                                    className="btn-edit-monto"
                                                                    onClick={() => handleEditarMonto(pago)}
                                                                    title="Editar monto"
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge badge-pago-${pago.estado}`}>
                                                            {pago.estado === 'pagado' ? '✓ Pagado' : '⏳ Pendiente'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {formatDateStringToDisplay(pago.fecha_pago)}
                                                    </td>
                                                    <td className="actions-pagos">
                                                        <button
                                                            className={`btn-toggle-pago ${pago.estado === 'pagado' ? 'pagado' : 'pendiente'}`}
                                                            onClick={() => handleTogglePago(pago)}
                                                            title={pago.estado === 'pagado' ? 'Marcar como pendiente' : 'Marcar como pagado'}
                                                        >
                                                            {pago.estado === 'pagado' ? <FaTimesCircle /> : <FaCheckCircle />}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="pagos-resumen">
                                    <div className="resumen-item">
                                        <span className="label">Total Pagado:</span>
                                        <span className="value success">
                                            {formatCurrency(
                                                pagosMensuales
                                                    .filter(p => p.estado === 'pagado')
                                                    .reduce((sum, p) => sum + parseFloat(p.monto), 0)
                                            )}
                                        </span>
                                    </div>
                                    <div className="resumen-item">
                                        <span className="label">Total Pendiente:</span>
                                        <span className="value warning">
                                            {formatCurrency(
                                                pagosMensuales
                                                    .filter(p => p.estado === 'pendiente')
                                                    .reduce((sum, p) => sum + parseFloat(p.monto), 0)
                                            )}
                                        </span>
                                    </div>
                                    <div className="resumen-item total">
                                        <span className="label">Total Anual:</span>
                                        <span className="value">
                                            {formatCurrency(
                                                pagosMensuales.reduce((sum, p) => sum + parseFloat(p.monto), 0)
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArriendosPage;
